/**
 * Persistence for `exam_jobs`. Mongo only: no handler imports, no readiness logic, no scheduling
 * policy — the runner owns those. Every status change goes through a function here so it emits its
 * transition hook exactly once.
 *
 * Two unique PARTIAL indexes carry the design:
 *  - `{lockKey}` while `status:'claimed'` — this IS the mutual exclusion, and its granularity is the
 *    parallelism knob. Everything that rewrites `exam.tasks` shares one key and therefore serialises;
 *    work confined to its own document carries its own key and runs alongside. The loser of a race gets
 *    a duplicate-key error and moves on. No lock collection, no lease on a second document.
 *  - `{dedupeKey}` while the job is live — replaces the ad-hoc "is it already running?" guards that were
 *    spread over the mutations (each of which could only see its own instance's work).
 */

import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { randomUUID } from 'node:crypto';
import type { BatchIntent } from './batch-store.js';
import { emitJobTransition } from './job-hooks.js';
import {
  defaultLockKey,
  EXAM_JOBS_COLLECTION,
  isTerminalJobStatus,
  type JobDoc,
  type JobPayload,
  type JobStatus,
} from './job.js';
import { DEFAULT_MAX_ATTEMPTS, LEASE_GRACE_MS, PRUNE_BATCH } from './limits.js';

const LIVE_STATUSES: JobStatus[] = ['pending', 'claimed'];

const MONGO_DUPLICATE_KEY = 11000;

const isDuplicateKeyError = (err: unknown): boolean =>
  !!err && typeof err === 'object' && (err as { code?: number }).code === MONGO_DUPLICATE_KEY;

const jobs = async () => getRawCollection<JobDoc>(EXAM_JOBS_COLLECTION);

/**
 * Idempotent — safe to call on every boot. Index creation is the only schema step this collection has;
 * without the two unique partials the exclusivity and dedupe guarantees silently do not exist, so the
 * runner refuses to start if this fails.
 */
export async function ensureJobIndexes(): Promise<void> {
  const collection = await jobs();

  // Every field `examIdsWithPendingJobs` touches, which is its only consumer: `status` and `runAfter` for the match,
  // `examId` for the group. That makes the whole pipeline COVERED — no document fetches — and covering it is worth
  // more here than anywhere else in this file, because `$group` is a blocking stage: it has to consume the entire
  // match before emitting anything, so the `$limit` cannot be pushed past it and every due row is processed on
  // every tick to return at most `EXAM_SCAN_LIMIT` ids.
  //
  // It previously ended in `priority: -1` instead, which the pipeline never reads, while `examId` — which it groups
  // on — was absent, so each due row had to be fetched off disk to answer a query about a field one key would have
  // carried.
  await collection.createIndex({ status: 1, runAfter: 1, examId: 1 }, { name: 'job_claim_scan' });
  await collection.createIndex({ examId: 1, status: 1 }, { name: 'job_by_exam' });
  // The per-exam scan's shape, ordered equality → sort/range to match `pendingJobsForExam`: `examId`/`status` are
  // equality and `runAfter` is both the sort key and the bound.
  //
  // A `priority: -1` key used to sit between them. Nothing ever set a priority — `reconcile.ts` was the only
  // producer and no type implemented `desired` — so the key never discriminated, and the sort it served was
  // `runAfter` alone with extra steps. The order still matters for what remains: `runAfter` BEFORE the equality
  // keys cannot serve the sort at all, because a range predicate is not stripped from the index prefix the way an
  // equality one is, and the planner is then left with a blocking in-memory sort of every pending row.
  await collection.createIndex({ examId: 1, status: 1, runAfter: 1 }, { name: 'job_exam_due_scan_v2' });
  // Exclusivity is per RESOURCE, not per exam: at most one claimed job may hold a given lockKey. Jobs
  // that rewrite the task tree share one key and serialise; work on its own documents (a submission)
  // carries its own key and runs alongside them.
  // Really smart this key ensures that every write that would violate the exclusivity constraint fails, and the runner moves on without ever having to read the row.
  await collection.createIndex(
    { lockKey: 1 },
    { name: 'job_one_claimed_per_lock', unique: true, partialFilterExpression: { status: 'claimed' } },
  );
  // Keyed on the `live` flag, not on `status: {$in: [...]}`: support for `$in` inside a
  // partialFilterExpression is version-dependent, and an index that silently fails to apply would take
  // the dedupe guarantee with it. An equality match on a boolean works everywhere.
  //
  // The flag also lets the dedupe WINDOW be a property of the type rather than of the status: `live` means "still
  // absorbs an equivalent enqueue", which for most types is "not terminal" and for a type whose output depends on
  // state it reads at run time ends at the claim (`dedupeUntil`). A status-keyed index could not express that.
  await collection.createIndex(
    { dedupeKey: 1 },
    { name: 'job_dedupe_live', unique: true, partialFilterExpression: { live: true } },
  );
  // The announcement's read: every row of one batch. Not unique — a batch is many rows, which is the point.
  await collection.createIndex({ batchKey: 1 }, { name: 'job_by_batch' });
  await collection.createIndex({ status: 1, deadlineAt: 1 }, { name: 'job_lease_sweep' });
  // The prune's shape, and it matters most in the case that looks harmless: when NOTHING is old enough to delete.
  // Without `finishedAt` in an index, `status` alone bounds the scan and the date is a post-fetch filter, so the
  // `.limit()` is never reached and every `done`/`cancelled` DOCUMENT in the collection is fetched to be discarded —
  // once every SWEEP_INTERVAL_MS, forever, growing with a week of retention. With it this is a range scan that
  // returns nothing immediately, and the `{_id: 1}` projection is covered by the index.
  await collection.createIndex({ status: 1, finishedAt: 1 }, { name: 'job_prune_scan' });

  // DROPPED EXPLICITLY, because removing a `createIndex` call removes nothing: `createIndex` is additive, so an
  // index this list no longer mentions survives on every deployment that already built it — still indexed on every
  // write, still consuming memory, for a query that no longer exists.
  //  - `job_batch_progress` covered the batch aggregation; completion is `anyLiveJobForTypes` now.
  //  - `job_exam_due_scan` carried a `priority` key that nothing ever set (see above); `_v2` replaces it.
  // Best-effort and per index, so a missing one (a fresh database) does not stop the others.
  for (const stale of ['job_batch_progress', 'job_exam_due_scan']) {
    try {
      await collection.dropIndex(stale);
      logger.info({ index: stale }, 'Dropped a job index that is no longer used');
    } catch {
      // Never created here, or already dropped. Nothing to do and nothing to report.
    }
  }

  logger.info({ collection: EXAM_JOBS_COLLECTION }, 'Job indexes ensured');
}

export interface EnqueueJobInput<P extends JobPayload = JobPayload> {
  examId: string;
  type: string;
  payload?: P;
  dedupeKey: string;
  /** Which run this row belongs to — see `JobDoc.batchKey`. Supplied by the dispatch, never by a call site. */
  batchKey?: string;
  /** Why this row is being created — see `JobDoc.intents`. Recorded on the row, or on the row this one dedupes onto. */
  intent?: BatchIntent;
  /** Resource held while running. Defaults to `exam:<examId>` (fully serial for that exam). */
  lockKey?: string;
  maxAttempts?: number;
  /** This row's runtime budget, overriding its type's default. For work whose size is known at enqueue time. */
  maxRuntimeMs?: number;
}

/**
 * Inserts a job unless an equivalent one is already live.
 *
 * Returns the job, or `null` when it was deduped — which is a normal outcome, not an error: a dispatch is allowed
 * to fire liberally, and a caller that needs to WAIT resolves the twin instead (`joinTwin` on a type's `batch`).
 *
 * The equivalence check is the implicit `dedupeKey` unique index, which is the only way to guarantee that two instances racing to enqueue the same work do not both succeed.
 * The runner's fairness and backoff guarantees are built on the assumption that a job is never duplicated, so this is a hard requirement.
 */
/**
 * Records one more trigger's ask against the row that absorbed it.
 *
 * THE LIVE ROW IF THERE IS ONE, the newest otherwise. The live row is the one that will still DO something, so a
 * cause recorded on it is a cause the work will actually reflect — which is exactly the pending case the dedupe is
 * correct for. Falling back to the newest matters at the edge: the twin can go terminal between the rejected insert
 * and this write, and a terminal row is still part of its wave (`jobsOfBatch` reads it), so the ask is still found
 * rather than dropped because the work had just been finished by someone else.
 *
 * BEST EFFORT, and bounded: the one thing that can still lose an intent is the wave draining and announcing in the
 * window between the two writes. That costs a cause in a message — never a unit of work, because no row reads
 * intents to decide what to do. It is logged rather than retried.
 */
async function pushIntent(examId: string, dedupeKey: string, intent: BatchIntent): Promise<void> {
  const collection = await jobs();
  try {
    // The live lookup is served by `job_dedupe_live`. The fallback is NOT — that index is partial on `live: true`, so
    // a query without it would scan the collection and sort in memory. `examId` bounds it to one exam's rows via
    // `job_by_exam`'s prefix, which is the difference between a bounded read and one that grows with retention.
    const target =
      (await collection.findOne({ dedupeKey, live: true }, { projection: { _id: 1 } })) ??
      (await collection.findOne({ examId, dedupeKey }, { projection: { _id: 1 }, sort: { createdAt: -1 } }));
    if (!target) {
      logger.warn({ examId, dedupeKey }, 'Deduped enqueue found no row to record its cause on');
      return;
    }
    await collection.updateOne({ _id: target._id }, { $push: { intents: intent } });
  } catch (err) {
    logger.error({ err, examId, dedupeKey }, 'Could not record a deduped trigger’s cause');
  }
}

export async function enqueueJob<P extends JobPayload = JobPayload>(
  input: EnqueueJobInput<P>,
): Promise<JobDoc<P> | null> {
  if (!input.examId) throw new Error(`enqueueJob(${input.type}): examId is required`);

  const now = new Date();
  const doc: JobDoc<P> = {
    _id: randomUUID(),
    examId: input.examId,
    type: input.type,
    payload: (input.payload ?? {}) as P,
    status: 'pending',
    live: true,
    lockKey: input.lockKey ?? defaultLockKey(input.examId),
    runAfter: now,
    dedupeKey: input.dedupeKey,
    ...(input.batchKey ? { batchKey: input.batchKey } : {}),
    ...(input.intent ? { intents: [input.intent] } : {}),
    result: null,
    ...(input.maxRuntimeMs ? { maxRuntimeMs: input.maxRuntimeMs } : {}),
    attempts: 0,
    maxAttempts: input.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
    lastGate: null,
    lastError: null,
    firstWaitAt: null,
    createdAt: now,
    finishedAt: null,
  };

  const collection = await jobs();
  try {
    await collection.insertOne(doc);
  } catch (err) {
    if (isDuplicateKeyError(err)) {
      logger.info({ examId: input.examId, type: input.type, dedupeKey: input.dedupeKey }, 'Job already queued');
      // THE ASK SURVIVES THE DEDUPE. This trigger produced no row, so without this its reason would vanish and the
      // wave would announce one cause where two applied. Pushed onto the row that absorbed it, which is where the
      // work is — `intents` exists as a field separate from the payload precisely so it may grow (see `JobDoc`).
      if (input.intent) await pushIntent(input.examId, input.dedupeKey, input.intent);
      return null;
    }
    throw err;
  }

  logger.info({ jobId: doc._id, examId: doc.examId, type: doc.type }, 'Job enqueued');
  return doc;
}

/**
 * Exams with due work, **most overdue first**.
 *
 * The order is the fairness guarantee, not a nicety: the scan is bounded, so an arbitrary order (what
 * `distinct` gave) means the exams past the limit are never reached at all while the ones before it keep
 * being served. Sorting makes starvation impossible — an exam that misses a pass only moves further up the
 * queue, because `now - runAfter` grows for every row in the set at the same rate.
 *
 * `runAfter` AND NOT `createdAt`, and the difference only shows up over months. Both are aging schemes; they
 * differ in when the clock starts. `createdAt` measures from creation and never resets, so a job parked behind
 * a gate that never opens — a teacher who never confirms a structure — outranks all real work forever. Those
 * accumulate monotonically: nothing in the system deletes an abandoned exam's pending job. Each one surfaces
 * only once per `WAIT_BACKOFF_MS` (roughly one tick in fifteen), so the cost is invisible at first and then
 * absolute — past a few hundred of them the scan limit is spent entirely on exams that will defer again, and
 * new uploads are never reached at all. `runAfter` restarts the clock at "became due", which is the question
 * the scan is actually asking, so a job that just re-entered the pool re-enters at the back and ages in from
 * there.
 *
 * It costs nothing anywhere else: `runAfter` only ever differs from `createdAt` for a row that was deferred or
 * retried, so every job on its first pass sorts identically under both.
 */
export async function examIdsWithPendingJobs(now: Date, limit: number): Promise<string[]> {
  const collection = await jobs();
  const rows = await collection
    .aggregate<{
      _id: string;
    }>([
      { $match: { status: 'pending', runAfter: { $lte: now } } },
      { $group: { _id: '$examId', mostOverdue: { $min: '$runAfter' } } },
      { $sort: { mostOverdue: 1 } },
      { $limit: limit },
    ])
    .toArray();

  return rows.map(row => row._id);
}

export interface ClaimedSnapshot {
  /** Locks held anywhere in the cluster — a job whose lock is in here cannot be claimed. */
  lockKeys: Set<string>;
}

/**
 * What the cluster is currently working on: the set of locks it holds, and nothing else.
 *
 * It used to also return `jobsByExam` and `totalClaimed` for the per-exam fairness share. Both are gone with the
 * share's move to bytes — fairness now reads `declaredHeapFor`, which is live rather than a per-tick snapshot and
 * responds to a handler refining its declaration. A count of claimed rows cannot: two jobs holding 40 MB each and two
 * holding 512 MB are the same number.
 *
 * ONE FIELD, so the projection is one field. Stale the moment it returns, by design: the unique index is the authority
 * on exclusivity, and this only avoids claim attempts that would bounce off it.
 */
export async function claimedSnapshot(): Promise<ClaimedSnapshot> {
  const collection = await jobs();
  const claimed = await collection.find({ status: 'claimed' }, { projection: { lockKey: 1 } }).toArray();
  return { lockKeys: new Set(claimed.map(job => job.lockKey)) };
}

/**
 * This exam's due jobs, highest priority then most overdue first.
 *
 * BOUNDED, because the runner can only ever start `maxJobsForExam` of them and the rest are re-read next tick
 * anyway. Unbounded, an exam with a few thousand per-submission grading jobs loaded and sorted the whole set
 * every two seconds. The limit is a slack multiple of what could start, so a run of jobs blocked on a held lock
 * or a closed gate does not hide the runnable ones behind them.
 *
 * Ordered by `runAfter`, for the reason spelled out on `examIdsWithPendingJobs`: the limit here is a bounded
 * window, so a block of long-deferred jobs must not be able to fill it ahead of this exam's runnable ones. Jobs
 * blocked on a HELD LOCK are untouched by this either way — the runner deliberately writes nothing for those, so
 * their `runAfter` stays at creation and they keep their place.
 */
export async function pendingJobsForExam(examId: string, now: Date, limit = 200): Promise<JobDoc[]> {
  const collection = await jobs();
  return collection
    .find({ examId, status: 'pending', runAfter: { $lte: now } })
    .sort({ runAfter: 1 })
    .limit(limit)
    .toArray();
}

export async function getJob(jobId: string): Promise<JobDoc | null> {
  const collection = await jobs();
  return collection.findOne({ _id: jobId });
}

/**
 * Takes the job if it is still pending AND its lockKey is free. `null` covers both losses — another
 * instance took this job, or something else already holds its lock — and both mean the same thing to
 * the caller: move on.
 *
 * `runtimeMs` is the already-resolved budget for this job (see `resolveJobRuntimeMs`), passed in rather than looked
 * up here so that this module keeps knowing nothing about definitions. It is written to the row as `deadlineAt` in
 * the same atomic update that takes the claim, which is what lets the sweeper stay a single indexed query.
 *
 * `releaseDedupe` comes in the same way and for the same reason — it is `dedupeUntil === 'claimed'` on the type,
 * resolved by the runner, which has the definition. Set, the claim also clears `live`, so this row stops absorbing
 * equivalent enqueues the moment it starts running: see `JobDefinition.dedupeUntil` for which types need that and
 * why joining a running row is loss rather than coalescing for them.
 */
export async function claimJob<P extends JobPayload>(
  job: JobDoc<P>,
  claimedBy: string,
  runtimeMs: number,
  options: { releaseDedupe?: boolean } = {},
): Promise<JobDoc<P> | null> {
  const collection = await jobs();
  const now = new Date();

  // One atomic round-trip. The earlier update-then-read left a window in which the row could move on between
  // the two, so the runner could execute against a document that was no longer the one it had claimed.
  let claimed: JobDoc<P> | null;
  try {
    // `@adornis/baseql` resolves mongodb 7.x, which returns the document itself (not a `{value}` wrapper).
    claimed = (await collection.findOneAndUpdate(
      { _id: job._id, status: 'pending' } as any,
      {
        $set: {
          status: 'claimed',
          claimedBy,
          claimedAt: now,
          // Written HERE and not by the worker: the row has to be self-describing the instant it becomes claimed,
          // because between this update and the worker's first line the process can already be gone.
          deadlineAt: new Date(now.getTime() + runtimeMs),
          firstWaitAt: null,
          lastGate: null,
          // In the SAME write as the claim, so there is no instant at which the row is both running and still
          // absorbing enqueues — that instant is the whole bug this exists for.
          ...(options.releaseDedupe ? { live: false } : {}),
        },
        $inc: { attempts: 1 },
      } as any,
      { returnDocument: 'after' },
    )) as JobDoc<P> | null;
  } catch (err) {
    if (isDuplicateKeyError(err)) return null; // the lock is held — the exclusivity index said no
    throw err;
  }

  if (!claimed) return null; // another instance took it first
  await emitJobTransition({ job: claimed as JobDoc, from: 'pending', to: 'claimed', reason: 'claimed' });
  return claimed;
}

/**
 * Proof that the writer still owns the claim it is writing about.
 *
 * `status: 'claimed'` alone does NOT prove ownership, and that gap is a data-loss bug: worker A claims a job and
 * hangs, the sweeper reclaims the lease, worker B claims it — the row is `claimed` again, by someone else. A then
 * wakes up and writes its outcome, matches on status, and silently overwrites B's live execution. Worse for
 * `requeueJob`: A flips the row back to `pending` while B is still running it, C claims it, and now two workers
 * hold one job. The `lockKey` index cannot help — it constrains one claimed row per key, not one worker per row.
 *
 * So every write that speaks for a claim carries the identity it is speaking for. A guard that no longer matches
 * makes the write a no-op, which is exactly right: the job has moved on without us.
 */
export interface ClaimGuard {
  /** Apply only while the row is still claimed by this worker. */
  claimedBy?: string | null;
  /**
   * Apply only while the claim is still the exact one that was read. The sweeper's reclaim needs this: a job can
   * finish, be re-enqueued and be re-claimed by the SAME instance between the read and the write, and `claimedBy`
   * alone would then match the new claim and requeue live work. `claimedAt` is immutable for the life of a claim,
   * which is what makes it usable as the claim's identity.
   */
  claimedAt?: Date | null;
}

/** Terminal completion. `note` is diagnostics; `skipped` is a normal, successful outcome. */
export async function completeJob<P extends JobPayload>(
  job: JobDoc<P>,
  outcome: { skipped?: boolean; note?: string; result?: Record<string, unknown> } = {},
  guard?: ClaimGuard,
): Promise<void> {
  await transition(job, 'done', {
    reason: outcome.skipped ? 'skipped' : 'completed',
    // The claim fields are cleared here exactly as `failJob` and `cancelJob` clear them. Nothing reads them on a
    // terminal row — every query filters on `status` first — but leaving `deadlineAt` set on a `done` row makes it
    // the one terminal state whose lease bookkeeping still looks live, in the one file where those fields decide
    // who may write.
    set: {
      finishedAt: new Date(),
      lastError: null,
      note: outcome.note ?? null,
      // WHAT THIS ROW DID, for the wave's aggregate — written here because this is the one write that happens
      // exactly once per row, after the handler and under the claim guard. See `JobDoc.result`.
      result: outcome.result ?? null,
      // PERSISTED, not just a transition reason. `done` covers both "did the work" and "decided there was none to
      // do", and an announcement aggregated over a wave must not name units nothing happened to — so the
      // distinction has to survive on the row rather than only in the hook's log line.
      skipped: !!outcome.skipped,
      claimedBy: null,
      claimedAt: null,
      deadlineAt: null,
    },
    guard,
  });
}

export async function failJob<P extends JobPayload>(job: JobDoc<P>, error: string, guard?: ClaimGuard): Promise<void> {
  await transition(job, 'failed', {
    reason: 'attempts-exhausted',
    error,
    set: { finishedAt: new Date(), lastError: error, claimedBy: null, claimedAt: null, deadlineAt: null },
    guard,
  });
}

/**
 * Back to pending after a failed attempt, a reclaimed lease, or a shutdown.
 *
 * `refundAttempt` undoes the increment `claimJob` made. It is for hand-backs that are NOT the job's fault —
 * a rolling deploy interrupting a long extraction is not an attempt at anything, and without the refund three
 * deploys during one exam exhaust `maxAttempts` and fail work that never actually went wrong. A lease that
 * expired is a different matter and keeps its attempt: something did go wrong there, even if we cannot see what.
 */
export async function requeueJob<P extends JobPayload>(
  job: JobDoc<P>,
  runAfter: Date,
  reason: string,
  options: { error?: string; guard?: ClaimGuard; refundAttempt?: boolean } = {},
): Promise<void> {
  const { error, guard, refundAttempt } = options;
  await transition(job, 'pending', {
    reason,
    error,
    set: {
      runAfter,
      claimedBy: null,
      claimedAt: null,
      deadlineAt: null,
      ...(error ? { lastError: error } : {}),
    },
    // Never below zero: a refund for a job that somehow was not claimed would corrupt the backoff index.
    ...(refundAttempt && job.attempts > 0 ? { inc: { attempts: -1 } } : {}),
    guard,
  });
}

/**
 * Not runnable yet. Stays pending — no status change, so no transition hook: "still waiting" is not an
 * event, and firing one every couple of seconds would make the hooks useless for anything.
 */
export async function deferJob<P extends JobPayload>(job: JobDoc<P>, runAfter: Date, because?: string): Promise<void> {
  const collection = await jobs();
  await collection.updateOne(
    { _id: job._id, status: 'pending' } as any,
    {
      $set: {
        runAfter,
        lastGate: because ?? null,
        ...(job.firstWaitAt ? {} : { firstWaitAt: new Date() }),
      },
    } as any,
  );
}

/** The reason to run is gone (or will never come). Terminal, but not a failure. */
export async function cancelJob<P extends JobPayload>(job: JobDoc<P>, reason: string): Promise<void> {
  await transition(job, 'cancelled', {
    reason,
    set: { finishedAt: new Date(), claimedBy: null, claimedAt: null, deadlineAt: null, lastGate: reason },
  });
}

/**
 * Cancels this exam's PENDING jobs. Used when an exam has vanished and its queued work can never become
 * runnable.
 *
 * Claimed rows are deliberately left alone. Cancelling one would match on `{_id, status:'claimed'}` and
 * succeed against a worker on another instance that is mid-execution — the exact failure the `ClaimGuard`
 * exists to prevent, arriving through the one path that used to bypass it. A claimed job whose exam is gone
 * fails on its own within one lease, and until it does the pending sweep has already removed everything the
 * scan could act on. The count of survivors is returned so the caller can say so.
 */
export async function cancelLiveJobsForExam(
  examId: string,
  reason: string,
): Promise<{ cancelled: number; stillClaimed: number }> {
  const collection = await jobs();
  const live = await collection.find({ examId, status: { $in: LIVE_STATUSES } }).toArray();

  let cancelled = 0;
  let stillClaimed = 0;
  for (const job of live) {
    if (job.status === 'claimed') {
      stillClaimed += 1;
      continue;
    }
    await cancelJob(job, reason);
    cancelled += 1;
  }
  return { cancelled, stillClaimed };
}

/**
 * Wake this exam's waiting jobs now instead of letting them sit out their backoff.
 *
 * Call it from whatever OPENS a gate (a confirmation mutation, an upload completing). Readiness is derived from
 * the exam, so the job collection never sees that write — without a nudge the only thing that notices is the next
 * wait-backoff expiry, and that backoff is deliberately long so that thousands of exams parked on a teacher cost
 * nothing. This is what keeps "long backoff" from also meaning "slow to react": the poll is the floor, the nudge
 * is the fast path.
 *
 * Only touches jobs waiting on a gate; it never pulls a retry backoff forward, because a retry is waiting on time,
 * not on a gate. (`firstWaitAt` is set by `deferJob` and cleared by `claimJob`, so a job that has run is excluded.)
 *
 * IT ONLY EVER PULLS A JOB FORWARD — `runAfter: {$gt: now}`. Without that bound a nudge rewrote `runAfter` on every
 * waiting row, including rows that were ALREADY DUE, and the scan orders exams by `{$min: runAfter}` ascending — so
 * rewriting a long-overdue job's `runAfter` to "now" makes its exam look the *least* overdue and pushes it to the
 * back of the window. A job that is already due needs no nudge; it is the deferred ones this exists for.
 *
 * One stray nudge was harmless and self-correcting as the job aged. REPEATED nudging is not: an exam nudged on every
 * write would have its waiting jobs continuously reset and could sit behind `EXAM_SCAN_LIMIT` other exams
 * indefinitely. That only bites once something calls this often — which nothing does today, since every call site is
 * hand-written — so this is a guard against the obvious future, not a fix for a live bug.
 *
 * It also makes the call idempotent, which it was not: the old update always wrote a NEW timestamp, so calling it
 * twice in a row wrote every matching row twice. Now the second call matches nothing.
 *
 * `now` and not `firstWaitAt`: backdating to when the job first waited would put a just-un-gated exam at the front,
 * which is defensible since a teacher is watching, but it needs a pipeline change and this is the cheap correct
 * version — the job becomes due immediately either way.
 */
/**
 * On boot: hand back every claim this HOST left behind. Returns how many were handed back.
 *
 * WHY THIS BEATS WAITING FOR THE LEASE. A killed process leaves its claims `claimed` with a `deadlineAt` in the
 * future, and the sweeper cannot touch them until `deadlineAt + LEASE_GRACE_MS` — up to half an hour for a grading
 * row, because the runtime budget is per type and grading's is the largest. Yet a fresh process on the same host
 * KNOWS those claims are dead: no process with this hostname is running yet except this one, and this one has
 * claimed nothing. So the wait buys nothing and costs the whole lease.
 *
 * HANDED BACK, NOT RECLAIMED — deliberately. Requeueing returns them to `pending` for the ordinary scan to pick up
 * again; it does not run them here. The load that killed the last process is precisely the load we should not
 * immediately recreate, and going through the queue means the rows come back spread over ticks and in scan order,
 * behind whatever else is due, rather than all at once as a startup burst. What it does NOT mean today is that
 * another instance might take them instead: every deployed environment is one Compose service with its own database
 * (`gradias`, `gradias-qa`, `gradias-staging`, one per test slug), so this process is the only reader of this
 * collection. The redistribution this enables is across TIME, not across hosts.
 *
 * THE ATTEMPT IS REFUNDED. These jobs were killed, not failed — the same rule the sweeper applies, and the reason
 * `requeueJob` has the option (see its doc).
 *
 * MATCHED ON THE HOSTNAME PREFIX, not the full `instanceId`, because that id carries a pid and a fresh uuid and so
 * changes on every restart — a process could never recognise its own predecessor by the whole string. The prefix is
 * enough here for a reason worth naming, because it is a DEPLOYMENT property and not a fact about this code: one job
 * process per job collection, per the paragraph above. A hostname prefix therefore selects our own predecessors and
 * nothing else. Two consequences: a container that gets a NEW hostname (a deploy recreates it; a restart does not)
 * leaves orphans this cannot see, so the sweeper stays the backstop; and if the runner ever moves into a second
 * process that shares this hostname AND this database, this would hand back that process's LIVE claims, so the
 * split has to give the runner either its own container or a claim identity this query can tell apart.
 *
 * IT ASKS NOTHING ABOUT LIVENESS, and there is nothing to ask: this runs before `startJobTicker`, so we hold no
 * claims of our own, and every claim bearing our hostname therefore belongs to a process that is definitively gone.
 * That certainty is exactly why this path REFUNDS the attempt while the sweeper's lease reclaim does not — a
 * predecessor being killed is not the job's fault, whereas the sweeper cannot tell a corpse from a runaway and no
 * longer guesses (see the reclaim in `sweeper.ts`).
 */
export async function handBackOwnOrphans(host: string): Promise<number> {
  if (!host) return 0;
  const collection = await jobs();
  // Anchored so a hostname that is a prefix of another cannot match it, and escaped because a hostname is not
  // guaranteed to be regex-safe.
  const prefix = new RegExp(`^${host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:`);
  const rows = await collection.find({ status: 'claimed', claimedBy: prefix }).toArray();
  if (rows.length === 0) return 0;

  // One at a time rather than an `updateMany`, so each row goes through `requeueJob` and gets its transition hook,
  // its attempt refund and its guard — the bookkeeping is the point, and there are only ever as many rows here as
  // one process could hold at once.
  for (const row of rows) {
    await requeueJob(row, new Date(), 'instance-restarted', {
      guard: { claimedBy: row.claimedBy ?? null, claimedAt: row.claimedAt ?? null },
      refundAttempt: true,
    });
  }
  return rows.length;
}

export async function nudgeExamJobs(examId: string): Promise<number> {
  const collection = await jobs();
  // Only `runAfter` — `lastGate` is left alone deliberately, because it is the answer to "why is this job not
  // running?" and overwriting it with the nudge's own reason would erase the only record of the closed gate.
  const now = new Date();
  const result = await collection.updateMany(
    { examId, status: 'pending', firstWaitAt: { $ne: null }, runAfter: { $gt: now } } as any,
    { $set: { runAfter: now } } as any,
  );
  return result.modifiedCount;
}

/**
 * Claims whose worker is gone: still `claimed` well past the point at which a live worker would have given up on
 * the job itself. In practice that means the process died — a running one enforces its own deadline locally and
 * writes its own outcome (see `execute`), so it never leaves a row here.
 *
 * Hence the `LEASE_GRACE_MS`: the extra margin is what turns this from "reclaim anything that ran too long", which
 * could take work away from a live instance, into "clean up after processes that are gone".
 *
 * KEYED ON THE ROW'S OWN `deadlineAt`, not on `claimedAt` plus a global constant. Once each type — and each row —
 * may declare its own budget, a single cutoff computed here would be wrong for everything that is not the default:
 * too early for a long grading run, which is the duplicate-execution bug the grace margin exists to prevent, and
 * needlessly late for everything short. Doing it instead by reading each claimed row's type and looking up its
 * definition would make this a full scan of claimed rows *and* would require the sweeping instance to have that
 * type registered, which during a rolling deploy it may not. Writing the deadline at claim time keeps this one
 * indexed range query that needs to understand nothing about job types at all.
 *
 * Nothing can refresh `deadlineAt` — that is the point, and it is why this is a real maximum runtime rather than a
 * lease. A heartbeat-renewed lease could be kept alive indefinitely by a hung handler, because the heartbeat ran on
 * its own timer and knew nothing about whether the work was progressing.
 */
export async function findExpiredLeases(now: Date, limit: number): Promise<JobDoc[]> {
  const collection = await jobs();
  const reclaimableBefore = new Date(now.getTime() - LEASE_GRACE_MS);
  return collection
    .find({ status: 'claimed', deadlineAt: { $lt: reclaimableBefore } })
    .limit(limit)
    .toArray();
}

/**
 * Drops old `done`/`cancelled` rows. **Never touches `failed`** — those are the record of what needs looking at.
 *
 * One `deleteMany` rather than find-then-delete-each. The loop existed only to fire an `onDelete` hook per row,
 * and the sole thing that ever reached that hook was this prune, seven days after the fact — a notification that
 * a finished job's bookkeeping was garbage-collected, which no consumer wanted. Deleting the hook made the batch
 * delete available, so this is now one round-trip instead of up to 500.
 */
export async function pruneFinishedJobs(olderThan: Date, limit = PRUNE_BATCH): Promise<number> {
  const collection = await jobs();
  // `deleteMany` has no limit option, so the batch bound is applied by selecting ids first. Still one delete.
  const stale = await collection
    .find(
      { status: { $in: ['done', 'cancelled'] }, finishedAt: { $lt: olderThan } },
      { projection: { _id: 1, examId: 1, batchKey: 1 } },
    )
    .limit(limit)
    .toArray();
  if (!stale.length) return 0;

  // A ROW WHOSE BATCH IS STILL OPEN IS NOT STALE, however old it is. The batch is what an announcement is
  // aggregated over, so deleting an early row of a run that has not finished makes that announcement UNDER-REPORT
  // — silently, which is the worst shape a wrong announcement can have. It takes a wave older than the retention
  // window to reach this, but the pipeline has exactly that case: a split confirmation waits on a teacher.
  const keep = await openBatchKeysOf(stale);
  const deletable = stale.filter(row => !row.batchKey || !keep.has(row.batchKey));
  if (!deletable.length) return 0;

  const result = await collection.deleteMany({ _id: { $in: deletable.map(row => row._id) } } as any);
  return result.deletedCount;
}

/**
 * Which of these rows' batches are still open — one query per exam in the prune batch, and only for rows that
 * carry a batch at all.
 *
 * Imported dynamically because `batch-store.ts` is a sibling with no reason to be loaded by every consumer of the
 * job store; the prune is the only reader here.
 */
async function openBatchKeysOf(rows: Array<{ examId?: string; batchKey?: string }>): Promise<Set<string>> {
  const examIds = [...new Set(rows.filter(row => row.batchKey).map(row => row.examId ?? ''))].filter(Boolean);
  if (!examIds.length) return new Set();

  const { openBatchKeys } = await import('./batch-store.js');
  const perExam = await Promise.all(examIds.map(examId => openBatchKeys(examId)));
  return new Set(perExam.flat());
}

/**
 * Moves rows onto a different batch — the repair for a dispatch whose batch closed underneath it.
 *
 * ONLY EVER ROWS THE CALLER JUST INSERTED. Re-stamping a row somebody else created would move their work between
 * waves, and a row already counted by a closed batch's announcement must stay where it was counted.
 */
export async function restampBatch(jobIds: readonly string[], batchKey: string): Promise<number> {
  if (!jobIds.length || !batchKey) return 0;
  const collection = await jobs();
  const result = await collection.updateMany({ _id: { $in: [...jobIds] } }, { $set: { batchKey } });
  return result.modifiedCount;
}

/**
 * Every row of one run, for the wave's announcement. See `JobDoc.batchKey`.
 *
 * WITHOUT `result.consistencyItems`, which is a deliberate exclusion rather than a micro-optimisation. That one
 * subfield is a per-node copy of a student's answer, so a cohort's rows carry tens of megabytes of it — and no
 * consumer of THIS function reads it: the announcement wants causes and graded ids, and the reconciliation that
 * does want the items asks for them by task (`consistencyItemsOfBatch`). Fetching a whole wave to build one
 * announcement should cost a wave's worth of metadata, not a wave's worth of prose.
 */
export async function jobsOfBatch(batchKey: string): Promise<JobDoc[]> {
  if (!batchKey) return [];
  const collection = await jobs();
  return collection.find({ batchKey }, { projection: { 'result.consistencyItems': 0 } }).toArray();
}

/**
 * The stored consistency items of one task's SUCCEEDED grading rows in a batch — the reconciliation's input.
 *
 * Filtered and projected in the query rather than in the caller, because the difference is the whole cost: a
 * ten-task wave asked ten times for "every row of the batch" would read the entire wave's items ten times over.
 * `done && !skipped` is `succeeded` (see `BatchOutcome`) — a skipped row graded nothing, so it has nothing to
 * reconcile.
 */
export async function consistencyItemsOfBatch(batchKey: string, taskKey: string): Promise<unknown[]> {
  if (!batchKey || !taskKey) return [];
  const collection = await jobs();
  const rows = await collection
    .find({ batchKey, type: 'grading', status: 'done', skipped: { $ne: true }, 'payload.taskKey': taskKey } as any, {
      projection: { 'result.consistencyItems': 1 },
    })
    .toArray();
  return rows.flatMap(row => {
    const items = (row.result as { consistencyItems?: unknown } | null | undefined)?.consistencyItems;
    return Array.isArray(items) ? items : [];
  });
}

/**
 * The status write itself, so `transition` below reads as one path.
 *
 * `null` is the ONE recoverable failure: becoming live again collided with an equivalent live row. That can only
 * happen to a row whose dedupe was released at the claim (`dedupeUntil: 'claimed'`) — it went running, an equivalent
 * row was enqueued behind it, and now a retry or an expired lease is trying to put this one back to `pending`. Two
 * live rows for one key is exactly what the unique index exists to prevent. Every other duplicate-key error is a real
 * one and is rethrown.
 */
async function applyTransition(
  filter: Record<string, unknown>,
  patch: Record<string, unknown>,
  inc: Record<string, number> | undefined,
  becomingLive: boolean,
): Promise<{ modifiedCount: number } | null> {
  const collection = await jobs();
  try {
    return await collection.updateOne(filter, { $set: patch, ...(inc ? { $inc: inc } : {}) });
  } catch (err) {
    if (!isDuplicateKeyError(err) || !becomingLive) throw err;
    return null;
  }
}

/**
 * Ends a row that tried to become live again and found an equivalent row already there.
 *
 * The equivalent row will do the work, and this one has done none of it — it was not re-admitted. Cancelling is
 * therefore the honest outcome and loses nothing of the WORK. Its causes are not lost either: they are on the row,
 * the row keeps its `batchKey`, and `jobsOfBatch` reads cancelled rows too — so the wave still knows it was asked.
 *
 * Cannot recurse: a cancel clears `live`, so it cannot collide.
 *
 * The terminal shape is `cancelJob`'s — `finishedAt` above all, or the row never prunes — and the caller's guard is
 * carried over: reaching here proves the filter matched, and the cancel should be no less careful about ownership
 * than the write that failed.
 */
async function supersede<P extends JobPayload>(
  job: JobDoc<P>,
  to: JobStatus,
  reason: string,
  guard?: ClaimGuard,
): Promise<void> {
  logger.info(
    { jobId: job._id, type: job.type, from: job.status, to, dedupeKey: job.dedupeKey },
    'Equivalent work is already queued — cancelling this row instead of re-queueing it',
  );
  await transition(job, 'cancelled', {
    reason: `superseded:${reason}`,
    set: { finishedAt: new Date(), claimedBy: null, claimedAt: null, deadlineAt: null, lastGate: 'superseded' },
    ...(guard ? { guard } : {}),
  });
}

/** Single writer for status changes, so the hook fires exactly once and always after the write. */
async function transition<P extends JobPayload>(
  job: JobDoc<P>,
  to: JobStatus,
  options: {
    reason: string;
    error?: string;
    set?: Record<string, unknown>;
    inc?: Record<string, number>;
    guard?: ClaimGuard;
  },
): Promise<void> {
  // `live` means "this row still absorbs an equivalent enqueue", which is what keys the dedupe index. It normally
  // mirrors the status — a terminal job that stayed `live` would block the next enqueue of the same work forever —
  // and `claimJob` is the one other writer, clearing it early for a type that declares `dedupeUntil: 'claimed'`.
  const becomingLive = !isTerminalJobStatus(to);
  const patch = { status: to, live: becomingLive, ...(options.set ?? {}) };
  // Status is not identity: see ClaimGuard for why matching on it alone lets a woken-up zombie overwrite the
  // worker that legitimately holds the job now.
  const filter = {
    _id: job._id,
    status: job.status,
    ...(options.guard?.claimedBy !== undefined ? { claimedBy: options.guard.claimedBy } : {}),
    ...(options.guard?.claimedAt !== undefined ? { claimedAt: options.guard.claimedAt } : {}),
  };

  const result = await applyTransition(filter, patch, options.inc, becomingLive);

  // SUPERSEDED: an equivalent row is live, so this one is cancelled rather than re-queued — the dedupe answer,
  // reached one step later than usual. See `applyTransition` for when this is possible at all.
  if (result === null) return supersede(job, to, options.reason, options.guard);

  // Lost the race (a sweeper reclaimed it, another instance finished it, this claim is no longer ours) — the
  // winner emitted its own transition, so emitting ours too would report a change that did not happen.
  if (!result.modifiedCount) {
    logger.info(
      { jobId: job._id, from: job.status, to, guarded: !!options.guard },
      'Job transition skipped — the row is no longer the one we read',
    );
    return;
  }

  // A job finishing is the most common way a gate opens — `masterSolutionExtracted` becomes true because the master
  // solution job wrote it. Nudging here rather than leaving it to the poll is what makes the long wait-backoff tail
  // safe: the tail only ever governs exams where genuinely nothing happened. Intrinsic, not a hook, so a new job
  // type cannot forget it.
  if (isTerminalJobStatus(to)) {
    // Non-fatal: the nudge is a latency optimisation over the wait backoff, never a correctness requirement.
    // Letting it throw would propagate out of `completeJob`, into the runner's catch, and requeue a job that
    // had already succeeded — a database hiccup turning into duplicate work.
    try {
      const woken = await nudgeExamJobs(job.examId);
      if (woken) logger.debug({ examId: job.examId, woken, after: job.type }, 'Woke waiting jobs after a transition');
    } catch (err) {
      logger.warn({ err, examId: job.examId }, 'Nudging waiting jobs failed — they will wait out their backoff');
    }
  }

  const updated = {
    ...job,
    ...patch,
    ...(options.inc?.attempts ? { attempts: job.attempts + options.inc.attempts } : {}),
  } as JobDoc<P>;
  // NO OUTSTANDING COUNT IS TAKEN HERE any more. Every terminal transition used to pay a `countDocuments` over
  // `{batchKey, status}` so a hook could ask "am I last?" without a query of its own — a cost on the hot path to
  // serve one question at the end of a run. A hook now asks `anyLiveJobForTypes` itself, which is the same shape
  // of query and runs once per finishing job instead of once per transition of any kind.
  await emitJobTransition({
    job: updated as JobDoc,
    from: job.status,
    to,
    reason: options.reason,
    ...(options.error ? { error: options.error } : {}),
  });
}

/**
 * The live row a dedupe collided with.
 *
 * `enqueueJob` returns null when the unique index rejects the insert, which tells a caller that the work is
 * queued but not WHICH row is doing it — and a caller that has to wait for its refine needs exactly that id.
 * Covered by `job_dedupe_live`, the same index that produced the collision.
 */
export async function findLiveJob(dedupeKey: string): Promise<JobDoc | null> {
  const collection = await jobs();
  return collection.findOne({ dedupeKey, live: true });
}

/**
 * Whether any job of `types` is still `pending` or `claimed` for this exam — THE completion predicate, and the
 * only thing that replaced the whole batch mechanism.
 *
 * A `batchKey` used to label the rows of one run, and the point of removing it is that the label was never load
 * bearing: the key was MINTED by asking this exact question (`liveBatchKeyForTypes` — "are any rows of these
 * types live? then join theirs") and a run was defined as open exactly while one of its rows was outstanding.
 * So "is my batch drained?" and "does this exam have no live rows of my step?" were the same predicate with a
 * shared string in between, and the string cost a query before every enqueue, an `EnqueueOptions` field, a
 * dedicated `{batchKey, status}` index, a count on every terminal transition, and a mint race that had to be
 * documented as harmless.
 *
 * WHAT A JOB TYPE HAS TO DO NOW: nothing. It asks "am I the last of my kind on this exam?" at its own terminal
 * transition, and writes COMPLETED if so. No run to open, no key to thread, no dispatcher contract to honour.
 *
 * TYPES, PLURAL, and getting the set wrong is silent — see `STEP_JOB_TYPES`. All three producers of one
 * artifact are one step, so an `msRefine` asking only about `msRefine` would announce the master solution
 * finished while an `msGeneration` is still queued.
 *
 * Served by `job_by_exam` (`{examId, status}`); `type` is a post-filter over a per-exam live set, which is
 * small by construction. `EXISTS` rather than a count: nothing needs the number, only whether it is zero.
 */
export async function anyLiveJobForTypes(examId: string, types: readonly string[]): Promise<boolean> {
  const collection = await jobs();
  const row = await collection.findOne(
    { examId, type: { $in: [...types] }, status: { $in: ['pending', 'claimed'] } },
    { projection: { _id: 1 } },
  );
  return row !== null;
}

/**
 * The same question, narrowed to rows whose payload matches — for ordering WITHIN a step.
 *
 * `gradingConsistency` reconciles one main task's grades across submissions, so it may only run once that TASK has
 * no grading row left. The exam-wide predicate is the wrong one for that: it would hold every task's consistency
 * behind every other task's grading, serialising a wave that has no reason to be serial.
 *
 * A PAYLOAD MATCH AND NOT A SECOND INDEX. The candidate set is one exam's live rows of one type — small by
 * construction, the same set `anyLiveJobForTypes` already post-filters — so the extra equality is a scan of a
 * handful of documents, not a collection scan. Keys are read as `payload.<key>`, so a caller states the field it
 * means and nothing here has to know what a task key is.
 */
export async function anyLiveJobForTypesWhere(
  examId: string,
  types: readonly string[],
  payloadMatch: Record<string, unknown>,
): Promise<boolean> {
  const collection = await jobs();
  const filter: Record<string, unknown> = {
    examId,
    type: { $in: [...types] },
    status: { $in: ['pending', 'claimed'] },
  };
  for (const [key, value] of Object.entries(payloadMatch)) filter[`payload.${key}`] = value;
  const row = await collection.findOne(filter, { projection: { _id: 1 } });
  return row !== null;
}
